/* eslint-disable no-debugger */
import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../common/components/dashboard_layout.component';
import { RanDBHeader } from './ran_db_header.component';
import RanDBTable from './ran_db_table.component';
import RanDBOverview from './ran_db_overview_new.component';
import UploadRanDBFileModal from './ran_db_upload_file_modal.component';
import RanDbUploadFileReview from './ran_db_upload_file_review_modal.component';
import { useAtomValue, useSetAtom, useAtom } from 'jotai';
import {
  allSitesRanDb,
  openAddInfoFormRanDb,
  openUploadFormRanDb,
  ranDbViewMode,
  ranDbArchivedType,
  ranDbTotalCount,
  ranDbTotalPages,
  ranDbCurrentPage,
  ranDbPageSize,
} from '../../common/atom/atoms';
import { HashRouter as Router, Route, Routes } from 'react-router-dom';
import { RanDBUploadForm } from './ran_db_upload_form_header.component';
import DeleteSiteModal from './delete_site_modal_ran_db.component';
import { AddInfoRanDBUploadForm } from './add_info_main.component';
import { getAllRanSites } from './functions/get_all_ran_sites.function';
import { getRanSitesByStatus } from './functions/get_ran_sites_by_status.function';
import { searchRanDbSites } from './functions/search_ran_db_sites.function';
import { RanDbDashboardProvider, useRanDbDashboard } from './context/ranDbDashboardContext';
// Removed backdrop import - now using table-level loader

// Inner component that uses the dashboard context
const RanDBContent = () => {
  const showAddInfoUploadForm = useAtomValue(openAddInfoFormRanDb);
  const showUploadForm = useAtomValue(openUploadFormRanDb);
  const viewMode = useAtomValue(ranDbViewMode);
  const archivedType = useAtomValue(ranDbArchivedType);
  // Removed backdrop usage - now using table-level loader
  const { fetchDashboardData } = useRanDbDashboard();

  const setAllRanSite = useSetAtom(allSitesRanDb);

  // Pagination state - using atoms so child components can update them
  const [currentPage, setCurrentPage] = useAtom(ranDbCurrentPage);
  const [pageSize, setPageSize] = useAtom(ranDbPageSize);
  const [totalCount, setTotalCount] = useAtom(ranDbTotalCount);
  const [totalPages, setTotalPages] = useAtom(ranDbTotalPages);
  const [isTableLoading, setIsTableLoading] = React.useState(false);
  const [selectedStatus, setSelectedStatus] = React.useState('All');
  const [showPagination, setShowPagination] = React.useState(true);

  // Search functionality state
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isSearching, setIsSearching] = React.useState(false);
  const [originalData, setOriginalData] = React.useState([]);
  const [originalPagination, setOriginalPagination] = React.useState({
    currentPage: 1,
    totalPages: 0,
    totalCount: 0,
    pageSize: 50,
  });

  // Sorting state
  const [sortBy, setSortBy] = React.useState('');
  const [sortOrder, setSortOrder] = React.useState('ASC');

  const fetchAllRanSites = async (
    page = currentPage,
    limit = pageSize,
    sortField = sortBy,
    sortDirection = sortOrder
  ) => {
    setIsTableLoading(true);
    try {
      let filters = {};
      if (viewMode === 'archived') {
        if (archivedType === 'all') {
          filters = {
            archived: true,
            page,
            limit,
            ...(sortField && { sortBy: sortField }),
            ...(sortDirection && { sortOrder: sortDirection }),
          };
        } else if (archivedType === 'Dismantle') {
          filters = {
            // archived: true,
            archived_type: 'Dismantled',
            page,
            limit,
            ...(sortField && { sortBy: sortField }),
            ...(sortDirection && { sortOrder: sortDirection }),
          };
        } else if (archivedType === 'Relocation') {
          filters = {
            // archived: true,
            archived_type: 'Relocation',
            page,
            limit,
            ...(sortField && { sortBy: sortField }),
            ...(sortDirection && { sortOrder: sortDirection }),
          };
        } else if (archivedType === 'Other') {
          filters = {
            // archived: true,
            archived_type: 'Other',
            page,
            limit,
            ...(sortField && { sortBy: sortField }),
            ...(sortDirection && { sortOrder: sortDirection }),
          };
        }
      } else {
        filters = {
          archived: false,
          page,
          limit,
          ...(sortField && { sortBy: sortField }),
          ...(sortDirection && { sortOrder: sortDirection }),
        };
      }
      const resp = await getAllRanSites(filters);

      // Handle pagination response
      if (resp?.pagination) {
        setTotalCount(resp.pagination.totalCount);
        setTotalPages(resp.pagination.totalPages);
        setCurrentPage(resp.pagination.currentPage);
        setPageSize(resp.pagination.limit);
      }

      setAllRanSite(resp?.data || []);

      // Store original data for search restoration
      if (!isSearching) {
        setOriginalData(resp?.data || []);
        setOriginalPagination({
          currentPage: resp?.pagination?.currentPage || 1,
          totalPages: resp?.pagination?.totalPages || 0,
          totalCount: resp?.pagination?.totalCount || 0,
          pageSize: resp?.pagination?.limit || 50,
        });
      }
    } catch (error) {
      console.error('Error fetching RanDB sites:', error);
    }
    setIsTableLoading(false);
  };

  const fetchRanSitesByStatus = async (status) => {
    setIsTableLoading(true);
    try {
      const resp = await getRanSitesByStatus(status);
      setAllRanSite(resp || []);
      setTotalCount(resp?.length || 0);
      setTotalPages(1);
      setCurrentPage(1);
    } catch (error) {
      console.error('Error fetching RanDB sites by status:', error);
    }
    setIsTableLoading(false);
  };

  // Search function
  const handleSearch = React.useCallback(
    async (siteCode) => {
      if (!siteCode || siteCode.trim() === '' || siteCode.trim().length <= 1) {
        handleClearSearch();
        return;
      }

      // Prevent duplicate searches
      if (searchQuery === siteCode) {
        return;
      }

      setSearchQuery(siteCode);
      setIsTableLoading(true);
      setIsSearching(true);
      try {
        const resp = await searchRanDbSites(siteCode);
        setAllRanSite(resp?.data || []);
        setTotalCount(resp?.data?.length || 0);
        setTotalPages(1);
        setCurrentPage(1);
        setShowPagination(false);
      } catch (error) {
        console.error('Error searching RanDB sites:', error);
        // On error, restore original data
        handleClearSearch();
      }
      setIsTableLoading(false);
    },
    [searchQuery]
  );

  // Clear search function
  const handleClearSearch = React.useCallback(() => {
    setIsSearching(false);
    setSearchQuery('');
    setAllRanSite(originalData);
    setCurrentPage(originalPagination.currentPage);
    setTotalPages(originalPagination.totalPages);
    setTotalCount(originalPagination.totalCount);
    setPageSize(originalPagination.pageSize);
    setShowPagination(true);
  }, [originalData, originalPagination]);

  useEffect(() => {
    fetchDashboardData();
    if (viewMode !== 'overview') {
      fetchAllRanSites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, archivedType]); // Re-fetch when view mode or archivedType changes

  // Sorting handler
  const handleSort = (field, direction) => {
    setSortBy(field);
    setSortOrder(direction);
    setCurrentPage(1); // Reset to first page when sorting
    fetchAllRanSites(1, pageSize, field, direction);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchAllRanSites(newPage, pageSize, sortBy, sortOrder);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
    fetchAllRanSites(1, newPageSize, sortBy, sortOrder);
  };

  // Status change handler
  const handleStatusChange = (newStatus) => {
    setSelectedStatus(newStatus);
    if (newStatus === 'All') {
      // Restore original paginated view
      setCurrentPage(1);
      setShowPagination(true);
      fetchAllRanSites(1, pageSize);
    } else {
      // Use status-specific API call
      setShowPagination(false);
      fetchRanSitesByStatus(newStatus);
    }
  };

  const renderContent = () => {
    if (showAddInfoUploadForm) {
      return <AddInfoRanDBUploadForm />;
    } else if (showUploadForm) {
      return <RanDBUploadForm />;
    } else {
      return (
        <>
          <RanDBHeader />
          {viewMode === 'overview' ? (
            <RanDBOverview />
          ) : (
            <RanDBTable
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={isTableLoading}
              selectedStatus={selectedStatus}
              onStatusChange={handleStatusChange}
              showPagination={showPagination}
              searchQuery={searchQuery}
              onSearch={handleSearch}
              onClearSearch={handleClearSearch}
              isSearching={isSearching}
              onSort={handleSort}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          )}
        </>
      );
    }
  };

  return (
    <>
      <DashboardLayout>
        {renderContent()}
        <UploadRanDBFileModal />
        <RanDbUploadFileReview
          onUploadComplete={async () => {
            await fetchAllRanSites();
            fetchDashboardData(true);
          }}
        />
        <DeleteSiteModal />
      </DashboardLayout>
    </>
  );
};

// Main component that provides the dashboard context
export const RanDBMain = () => {
  return (
    <RanDbDashboardProvider>
      <RanDBContent />
    </RanDbDashboardProvider>
  );
};
